/**
 * 汇率历史按钮 + Modal —— Client 端公用组件
 *
 * 用法：<RateHistoryButton />
 *
 * 行为：
 *   - 一个小按钮 "📈 历史"
 *   - 点击后弹 modal，展示最近 24h 每小时两条线：
 *     · 汇率 (中行现汇卖出价)
 *     · 成交价 (× (1 + markup))
 */
import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import api from '../services/api';

const GOLD = '#B8892A';
const BD = '#E8E0D0';
const T0 = '#110E0A';
const T2 = '#3A2F22';
const T3 = '#6B5E4E';
const T4 = '#A8998A';
const GRN = '#2A6645';
const RED = '#9B2335';
const MONO = "'DM Mono', monospace";

export default function RateHistoryButton({ buttonStyle, label = '📈 历史' }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [markup, setMarkup] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [hist, cfg] = await Promise.all([
          api.get('/exchange-rates/realtime/history', { params: { unit: 'hour' } }),
          api.get('/exchange-rates/config'),
        ]);
        if (cancelled) return;
        const m = Number(cfg.data?.markup_pct || 0);
        setMarkup(m);
        const rows = Array.isArray(hist.data) ? hist.data : [];
        setData(rows.map(r => ({
          bucket: r.bucket?.slice(11, 16) || r.bucket,
          rate: Number(r.avg_rate),
          effective: Number(r.avg_rate) * (1 + m / 100),
        })));
      } catch (_) {
        if (!cancelled) setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={{
          padding: '2px 8px', fontSize: 11, color: GOLD,
          background: 'rgba(184,137,42,.08)', border: `1px solid ${BD}`,
          borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap',
          fontFamily: 'var(--sans)',
          ...buttonStyle,
        }}
      >{label}</button>

      {open && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,14,10,.45)', zIndex: 1300,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(3px)',
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 4, width: 620, maxWidth: '94vw',
            padding: 20, boxShadow: '0 8px 32px rgba(0,0,0,.12)',
            border: `1px solid ${BD}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: T0, fontFamily: 'var(--serif)', letterSpacing: 1 }}>
                JPY/CNY 汇率历史（最近 24 小时）
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{ padding: '4px 10px', border: `1px solid ${BD}`, borderRadius: 3, background: '#fff', color: T2, fontSize: 12, cursor: 'pointer' }}
              >关闭</button>
            </div>
            <div style={{ fontSize: 11, color: T3, marginBottom: 12, fontFamily: MONO }}>
              数据源: 中国银行 · 当前 Markup: {markup}%
            </div>
            <div style={{ height: 280 }}>
              {loading ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T4, fontSize: 13 }}>加载中…</div>
              ) : data.length === 0 ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T4, fontSize: 13 }}>暂无数据</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={BD} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: T3 }} />
                    <YAxis
                      domain={['auto', 'auto']}
                      tick={{ fontSize: 11, fill: T3 }}
                      tickFormatter={(v) => Number(v).toFixed(6)}
                      width={75}
                    />
                    <Tooltip
                      formatter={(v) => Number(v).toFixed(6)}
                      contentStyle={{ fontSize: 12, fontFamily: MONO }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="rate" name="汇率（中行）" stroke={GRN} strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="effective" name="成交价（×Markup）" stroke={RED} strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
